import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { RefreshButton } from "@ui/components/buttons/button";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { VIDEO_PLAYER_ID } from "@ui/hooks/use-video";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { useState, VideoHTMLAttributes } from "react";

interface VideoPlayerProps extends VideoHTMLAttributes<HTMLVideoElement> {
	url?: string;
	title?: string;
	displayVideoGradient?: boolean;
}

export const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
	({ title, url, displayVideoGradient = true, ...videoProps }, ref) => {
		const { format } = useI18n();
		const [error, setError] = useState(false);
		const [isLoading, setIsLoading] = useState(true);

		return (
			<VideoWrapper>
				<Title className="p-size-l-bold">{title}</Title>
				{error ? (
					<EmptyContainer>
						<ErrorText className="p-size-m">{format("videoPlayer.error")}</ErrorText>
						<RefreshButton onClick={() => setError(false)} />
					</EmptyContainer>
				) : (
					<>
						{displayVideoGradient && <Gradient />}
						<StyledVideo
							isLoading={isLoading}
							id={VIDEO_PLAYER_ID}
							ref={ref}
							{...videoProps}
							onErrorCapture={e => {
								console.error(e);
								setError(true);
							}}
							muted
							controlsList="nodownload"
							onLoadedData={() => setIsLoading(false)}
						>
							<source src={url} />
						</StyledVideo>
						{isLoading && (
							<CenteredContainer>
								<LoadingContainer />
							</CenteredContainer>
						)}
					</>
				)}
			</VideoWrapper>
		);
	},
);

VideoPlayer.displayName = "VideoPlayer";
const VideoWrapper = styled.div`
	width: 100%;
	position: relative;
	padding-bottom: 56.25%;
`;

const StyledVideo = styled.video<{ isLoading: boolean }>`
	position: absolute;
	width: 100%;
	height: 100%;
	visibility: ${({ isLoading }) => (isLoading ? "hidden" : "visible")};
`;

const CenteredContainer = styled.div`
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
`;

const EmptyContainer = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	padding: 32px;
	background-color: ${colors.black};

	${stack(32, "center", "center")}
`;

const ErrorText = styled.div`
	color: ${colors.white};
	text-align: center;
`;

const Title = styled.div`
	position: absolute;
	top: 32px;
	left: 16px;
	right: 70px;
	color: ${colors.black};
	z-index: 20;
	pointer-events: none;
`;

const Gradient = styled.div`
	pointer-events: none;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 5;
	height: 95px;
	background: linear-gradient(180deg, ${colors.videoBackground} 0%, ${colors.transparent} 100%);
`;
